import { useState, useCallback } from 'react';
import Papa from 'papaparse';
import toast from 'react-hot-toast';
import { CsvRowData } from '../types/import'; // Import shared type

interface UseCsvParserResult {
  file: File | null;
  csvHeaders: string[];
  csvData: CsvRowData[]; // Array of objects with header keys
  isParsing: boolean;
  parseError: string | null;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  resetCsvData: () => void;
}

export function useCsvParser(): UseCsvParserResult {
  const [file, setFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<CsvRowData[]>([]);
  const [isParsing, setIsParsing] = useState<boolean>(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const resetCsvData = useCallback(() => {
    setFile(null);
    setCsvHeaders([]);
    setCsvData([]);
    setParseError(null);
    // Reset the file input visually if possible (requires ref or other method)
    // For now, just resetting state.
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      resetCsvData();
      return;
    }

    if (selectedFile.type !== 'text/csv') {
      toast.error('Por favor, selecione um arquivo CSV.');
      resetCsvData();
      event.target.value = ''; // Reset file input
      return;
    }

    setFile(selectedFile);
    setIsParsing(true);
    setParseError(null);
    setCsvHeaders([]);
    setCsvData([]);

    Papa.parse<CsvRowData>(selectedFile, {
      header: true, // Use the first row as headers
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          console.error("Erro ao parsear CSV:", results.errors);
          const errorMsg = results.errors.map(e => e.message).join('; ');
          setParseError(`Erro ao ler o CSV: ${errorMsg}`);
          toast.error(`Erro ao ler o CSV: ${errorMsg}`);
          resetCsvData();
        } else if (!results.meta.fields || results.meta.fields.length === 0) {
          const errorMsg = 'O arquivo CSV não contém cabeçalhos válidos.';
          setParseError(errorMsg);
          toast.error(errorMsg);
          resetCsvData();
        } else {
          setCsvHeaders(results.meta.fields || []);
          setCsvData(results.data);
          setParseError(null);
          toast.success('Arquivo CSV lido com sucesso!');
        }
        setIsParsing(false);
      },
      error: (error: Error) => {
        console.error("Erro fatal ao parsear CSV:", error);
        const errorMsg = `Erro fatal ao ler o CSV: ${error.message}`;
        setParseError(errorMsg);
        toast.error(errorMsg);
        setIsParsing(false);
        resetCsvData();
      }
    });

    // Important: Clear the input value after processing to allow re-uploading the same file
    event.target.value = '';

  }, [resetCsvData]);

  return {
    file,
    csvHeaders,
    csvData,
    isParsing,
    parseError,
    handleFileChange,
    resetCsvData,
  };
} 